import { useMemo } from "react"
import { DateTime } from "luxon"
import { useField } from "formik"
import { Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../../../../modules/localization/components/AppLocalized"
import { SelectedPriceListItem } from "../../../../../createOrderTypes"
import { CreateUpdateDeliveryFormValues } from "../../CreateUpdateDeliveryTimePopover"
import { BookingDateMismatchMessage } from "./BookingDateMismatchMessage"
import { OpeningHoursHintMessage } from "./OpeningHoursHintMessage"
import { DeliveryTimesFieldArray } from "./DeliveryTimesFieldArray"
import { FormikSubmitButton } from "../../../../../../forms/Formik"
import { useDeliveryTimeLimits } from "./useDeliveryTimeLimits"
import { DeliveryTimeDay } from "../../../../form/formTypes"
import { DeliveryDatePicker } from "./DeliveryDatePicker"

type PopoverDeliveryTimeFormContentProps = Readonly<{
    priceList: SelectedPriceListItem
    allowedWeekdays: number[]
    eventStartTime: DateTime | null
    closingDates: string[]
    openingHours?: DeliveryTimeDay[]
    maxAllowedDeliveries: number
    handlePopoverClose: () => void
    allowPastDates: boolean
}>

export function PopoverDeliveryTimeFormContent({
    priceList,
    allowedWeekdays,
    eventStartTime,
    closingDates,
    openingHours = [],
    maxAllowedDeliveries,
    handlePopoverClose,
    allowPastDates,
}: PopoverDeliveryTimeFormContentProps) {
    const [{ value: deliveryDate }] = useField<CreateUpdateDeliveryFormValues["deliveryDate"]>("deliveryDate")

    const { limits, noSlotsForDate } = useDeliveryTimeLimits(deliveryDate, openingHours)

    // A meeting that starts outside the shop's delivery window does not prevent the order:
    // the user is told when the shop delivers and picks a time inside that window instead.
    const isEventStartOutsideDeliveryHours = useMemo(() => {
        if (!eventStartTime || !limits) return false

        const eventTimeOnDeliveryDate = limits.minTime.set({
            hour: eventStartTime.hour,
            minute: eventStartTime.minute,
            second: 0,
            millisecond: 0,
        })

        return eventTimeOnDeliveryDate < limits.minTime || eventTimeOnDeliveryDate > limits.maxTime
    }, [eventStartTime?.toISO(), limits?.minTime.toISO(), limits?.maxTime.toISO()])

    // The only genuinely blocking case: the delivery date is today and the shop has already
    // closed, so no valid time is left. Picking another date reopens the form.
    const isDeliveryBlocked = noSlotsForDate

    const isDeliveryOnAnotherDayThanBooking = useMemo(() => {
        if (!eventStartTime || !deliveryDate) return false

        return !deliveryDate.hasSame(eventStartTime, "day")
    }, [eventStartTime?.toISO(), deliveryDate?.toISO()])

    return (
        <Stack gap="2rem">
            <Stack gap="1.25rem">
                <LocalizedStrict id="create-order-delivery-times-add-delivery-time-title">
                    <Typography variant="h6">Add delivery time</Typography>
                </LocalizedStrict>

                <DeliveryDatePicker
                    closingDates={closingDates}
                    priceList={priceList}
                    allowedWeekdays={allowedWeekdays}
                    isDeliveryBlocked={isDeliveryBlocked}
                    allowPastDates={allowPastDates}
                />

                {isDeliveryOnAnotherDayThanBooking && eventStartTime && deliveryDate ? (
                    <BookingDateMismatchMessage bookingStart={eventStartTime} deliveryDate={deliveryDate} />
                ) : (
                    // Only meaningful while both sit on the same day: comparing the meeting's
                    // clock time against another day's opening hours says nothing useful, and
                    // the mismatch message already carries the booking time.
                    isEventStartOutsideDeliveryHours &&
                    eventStartTime &&
                    limits && <OpeningHoursHintMessage bookingStart={eventStartTime} openingLimits={limits} />
                )}

                <DeliveryTimesFieldArray
                    openingHours={openingHours}
                    isDeliveryBlocked={isDeliveryBlocked}
                    maxAllowedDeliveries={maxAllowedDeliveries}
                />
            </Stack>
            <Stack flexDirection="row" justifyContent="center">
                <LocalizedStrict
                    id={
                        isDeliveryBlocked
                            ? "create-order-delivery-times-dialog-close-button"
                            : "create-order-delivery-times-dialog-submit-button"
                    }
                >
                    <FormikSubmitButton
                        type={isDeliveryBlocked ? "button" : "submit"}
                        fullWidth={false}
                        onClick={() => (isDeliveryBlocked ? handlePopoverClose() : {})}
                    >
                        {isDeliveryBlocked ? "Ok" : "Confirm"}
                    </FormikSubmitButton>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
